import React, { useState, useCallback, useRef, useMemo } from 'react';
import {
  ReactFlow,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Handle,
  Position,
  MarkerType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Zap, Mail, Phone, Clock, CheckSquare, Activity,
  MessageSquare, AlertTriangle, FileCheck, ClipboardCheck,
  Trash2, GripVertical, Save, X, MessageCircle, Linkedin,
} from 'lucide-react';

// ─── Node type definitions ──────────────────────────────
const NODE_TYPE_CONFIG = {
  trigger: { icon: Zap, label: 'Trigger', color: '#10b981', bg: '#10b981' },
  email: { icon: Mail, label: 'Send Email', color: '#3b82f6', bg: '#3b82f6' },
  ai_call: { icon: Phone, label: 'AI Voice Call', color: '#22c55e', bg: '#22c55e' },
  wait: { icon: Clock, label: 'Wait / Delay', color: '#f59e0b', bg: '#f59e0b' },
  task: { icon: CheckSquare, label: 'Create Task', color: '#8b5cf6', bg: '#8b5cf6' },
  update_status: { icon: Activity, label: 'Update Status', color: '#06b6d4', bg: '#06b6d4' },
  notify: { icon: MessageSquare, label: 'Notification', color: '#ec4899', bg: '#ec4899' },
  condition: { icon: AlertTriangle, label: 'Condition', color: '#f97316', bg: '#f97316' },
  request_docs: { icon: FileCheck, label: 'Request Docs', color: '#10b981', bg: '#10b981' },
  check_docs: { icon: ClipboardCheck, label: 'Check Docs', color: '#14b8a6', bg: '#14b8a6' },
  whatsapp: { icon: MessageCircle, label: 'WhatsApp Message', color: '#25D366', bg: '#25D366' },
  linkedin: { icon: Linkedin, label: 'LinkedIn Action', color: '#0A66C2', bg: '#0A66C2' },
};

const TRIGGER_TYPES = [
  { value: 'stage_change', label: 'Stage Change' },
  { value: 'new_lead', label: 'New Lead Created' },
  { value: 'score_change', label: 'Score Change' },
  { value: 'email_event', label: 'Email Event' },
  { value: 'call_completed', label: 'Call Completed' },
  { value: 'time_based', label: 'Time Based' },
  { value: 'manual', label: 'Manual' },
];

// ─── Custom Trigger Node ─────────────────────────────────
function TriggerNode({ data }) {
  return (
    <div
      className="px-4 py-3 rounded-xl border-2 shadow-lg min-w-[180px]"
      style={{ borderColor: '#10b981', background: 'var(--card)' }}
      data-testid="flow-trigger-node"
    >
      <div className="flex items-center gap-2 mb-2">
        <div className="p-1.5 rounded-lg" style={{ background: '#10b98120' }}>
          <Zap className="h-4 w-4" style={{ color: '#10b981' }} />
        </div>
        <span className="text-sm font-bold">Trigger</span>
      </div>
      <div className="text-xs text-muted-foreground">{data.triggerLabel || 'Configure trigger'}</div>
      <Handle type="source" position={Position.Bottom} className="!bg-emerald-500 !w-3 !h-3 !border-2 !border-background" />
    </div>
  );
}

// ─── Custom Action Node ──────────────────────────────────
function ActionNode({ data, id }) {
  const config = NODE_TYPE_CONFIG[data.nodeType] || NODE_TYPE_CONFIG.task;
  const Icon = config.icon;

  return (
    <div
      className="px-4 py-3 rounded-xl border-2 shadow-md min-w-[180px] group relative"
      style={{ borderColor: config.color + '80', background: 'var(--card)' }}
      data-testid={`flow-action-node-${id}`}
    >
      <Handle type="target" position={Position.Top} className="!w-3 !h-3 !border-2 !border-background" style={{ background: config.color }} />
      <div className="flex items-center gap-2 mb-1.5">
        <div className="p-1.5 rounded-lg" style={{ background: config.color + '20' }}>
          <Icon className="h-4 w-4" style={{ color: config.color }} />
        </div>
        <span className="text-sm font-semibold">{data.label || config.label}</span>
        {data.onDelete && (
          <button
            onClick={(e) => { e.stopPropagation(); data.onDelete(id); }}
            className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-destructive/20"
            data-testid={`flow-delete-node-${id}`}
          >
            <Trash2 className="h-3 w-3 text-destructive" />
          </button>
        )}
      </div>
      {data.subtitle && (
        <div className="text-xs text-muted-foreground">{data.subtitle}</div>
      )}
      <Handle type="source" position={Position.Bottom} className="!w-3 !h-3 !border-2 !border-background" style={{ background: config.color }} />
    </div>
  );
}

// ─── Condition Node (diamond-style) ──────────────────────
function ConditionNode({ data, id }) {
  return (
    <div
      className="px-4 py-3 rounded-xl border-2 shadow-md min-w-[180px] group relative"
      style={{ borderColor: '#f9731680', background: 'var(--card)' }}
      data-testid={`flow-condition-node-${id}`}
    >
      <Handle type="target" position={Position.Top} className="!bg-orange-500 !w-3 !h-3 !border-2 !border-background" />
      <div className="flex items-center gap-2 mb-1.5">
        <div className="p-1.5 rounded-lg bg-orange-500/20">
          <AlertTriangle className="h-4 w-4 text-orange-500" />
        </div>
        <span className="text-sm font-semibold">{data.label || 'Condition'}</span>
        {data.onDelete && (
          <button
            onClick={(e) => { e.stopPropagation(); data.onDelete(id); }}
            className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-destructive/20"
          >
            <Trash2 className="h-3 w-3 text-destructive" />
          </button>
        )}
      </div>
      <div className="flex justify-between text-xs text-muted-foreground mt-1">
        <Badge variant="outline" className="text-emerald-500 border-emerald-500/30">Yes</Badge>
        <Badge variant="outline" className="text-red-500 border-red-500/30">No</Badge>
      </div>
      <Handle type="source" position={Position.Bottom} id="yes" className="!bg-emerald-500 !w-3 !h-3 !border-2 !border-background !left-[30%]" />
      <Handle type="source" position={Position.Bottom} id="no" className="!bg-red-500 !w-3 !h-3 !border-2 !border-background !left-[70%]" />
    </div>
  );
}

const nodeTypes = {
  trigger: TriggerNode,
  action: ActionNode,
  condition: ConditionNode,
};

// ─── Convert workflow data to React Flow format ──────────
function workflowToFlow(workflow, onDeleteNode) {
  if (!workflow) return { nodes: [], edges: [] };

  const nodes = [];
  const edges = [];
  const wfNodes = workflow.nodes || [];

  // Trigger node
  nodes.push({
    id: 'trigger',
    type: 'trigger',
    position: { x: 250, y: 30 },
    data: {
      triggerLabel: workflow.trigger?.type
        ? (TRIGGER_TYPES.find(t => t.value === workflow.trigger.type)?.label || workflow.trigger.type)
        : 'Configure',
    },
    draggable: true,
  });

  // Action nodes
  wfNodes.forEach((node, i) => {
    const isCondition = node.type === 'condition';
    nodes.push({
      id: node.id,
      type: isCondition ? 'condition' : 'action',
      position: { x: 250, y: 130 + i * 120 },
      data: {
        nodeType: node.type,
        label: node.label || NODE_TYPE_CONFIG[node.type]?.label || node.type,
        subtitle: node.type === 'wait' ? `${node.config?.hours || 24}h delay` :
                  node.type === 'email' ? (node.config?.subject || 'Email step') :
                  node.type === 'whatsapp' ? 'WhatsApp message' :
                  node.type === 'linkedin' ? 'LinkedIn action' :
                  node.type === 'ai_call' ? 'Voice call' : '',
        onDelete: onDeleteNode,
      },
      draggable: true,
    });

    // Edge from previous node
    const sourceId = i === 0 ? 'trigger' : wfNodes[i - 1].id;
    edges.push({
      id: `e-${sourceId}-${node.id}`,
      source: sourceId,
      target: node.id,
      animated: true,
      style: { stroke: NODE_TYPE_CONFIG[node.type]?.color || '#666', strokeWidth: 2 },
      markerEnd: { type: MarkerType.ArrowClosed, color: NODE_TYPE_CONFIG[node.type]?.color || '#666' },
    });
  });

  return { nodes, edges };
}

// ─── Main Component ──────────────────────────────────────
export default function WorkflowFlowBuilder({ workflow, onChange, pipelineStages }) {
  const onDeleteNode = useCallback((nodeId) => {
    if (!workflow || !onChange) return;
    const updated = {
      ...workflow,
      nodes: (workflow.nodes || []).filter(n => n.id !== nodeId),
    };
    onChange(updated);
  }, [workflow, onChange]);

  const { nodes: initialNodes, edges: initialEdges } = useMemo(
    () => workflowToFlow(workflow, onDeleteNode),
    [workflow, onDeleteNode]
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const reactFlowWrapper = useRef(null);

  // Sync when workflow changes externally
  React.useEffect(() => {
    const { nodes: newNodes, edges: newEdges } = workflowToFlow(workflow, onDeleteNode);
    setNodes(newNodes);
    setEdges(newEdges);
  }, [workflow, onDeleteNode, setNodes, setEdges]);

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge({ ...params, animated: true, style: { strokeWidth: 2 } }, eds)),
    [setEdges]
  );

  const addNodeToWorkflow = (type) => {
    if (!workflow || !onChange) return;
    const newId = `n${Date.now()}`;
    const nodeConfig = NODE_TYPE_CONFIG[type] || {};
    const newNode = {
      id: newId,
      type,
      label: nodeConfig.label || type,
      config: type === 'wait' ? { hours: 24 } : {},
      next_node: null,
    };
    // Link previous last node to new node
    const existingNodes = [...(workflow.nodes || [])];
    if (existingNodes.length > 0) {
      existingNodes[existingNodes.length - 1].next_node = newId;
    }
    onChange({ ...workflow, nodes: [...existingNodes, newNode] });
  };

  // Draggable node palette
  const nodeTypes_list = Object.entries(NODE_TYPE_CONFIG).filter(([k]) => k !== 'trigger');

  return (
    <div className="flex flex-col h-full" data-testid="workflow-flow-builder">
      {/* Node Palette */}
      <div className="flex flex-wrap gap-1.5 p-3 border-b border-border bg-muted/30">
        <span className="text-xs font-medium text-muted-foreground mr-2 self-center">Add Step:</span>
        {nodeTypes_list.map(([key, config]) => {
          const Icon = config.icon;
          return (
            <Button
              key={key}
              variant="outline"
              size="sm"
              className="h-7 text-xs gap-1.5 hover:border-primary/50"
              onClick={() => addNodeToWorkflow(key)}
              data-testid={`flow-add-${key}`}
            >
              <Icon className="h-3 w-3" style={{ color: config.color }} />
              {config.label}
            </Button>
          );
        })}
      </div>

      {/* Flow Canvas */}
      <div style={{ width: '100%', height: '400px' }} ref={reactFlowWrapper}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          defaultViewport={{ x: 0, y: 0, zoom: 0.9 }}
          proOptions={{ hideAttribution: true }}
          className="bg-background"
        >
          <Controls className="!bg-card !border-border !shadow-lg" />
          <Background gap={20} size={1} color="var(--border)" />
        </ReactFlow>
      </div>
    </div>
  );
}
